import React, { useState, useEffect, useCallback } from 'react';
import { createAnnotation, getAnnotation, deleteAnnotation } from '../api';
import { ConfirmDialog } from './shared/UIComponents';

const PredictionAnnotation = ({ compoundId, onAnnotationChange }) => {
  const [annotation, setAnnotation] = useState('');
  const [notes, setNotes] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const loadExistingAnnotation = useCallback(async () => {
    try {
      const data = await getAnnotation(compoundId);
      if (data.annotation) {
        setAnnotation(data.annotation);
        setNotes(data.notes || '');
      }
    } catch (error) {
      console.error('Failed to load annotation:', error);
    }
  }, [compoundId]);

  // Load existing annotation when component mounts
  useEffect(() => {
    loadExistingAnnotation();
  }, [compoundId, loadExistingAnnotation]);

  const handleSave = async (newAnnotation) => {
    setLoading(true);
    try {
      await createAnnotation({
        compound_id: compoundId,
        annotation: newAnnotation,
        notes: notes.trim() || null,
      });
      
      setAnnotation(newAnnotation);
      setIsEditing(false);
      
      if (onAnnotationChange) {
        onAnnotationChange({ annotation: newAnnotation, notes: notes.trim() || null });
      }
    } catch (error) {
      console.error('Error saving annotation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteAnnotation(compoundId);
      setAnnotation('');
      setNotes('');
      if (onAnnotationChange) {
        onAnnotationChange(null);
      }
    } catch (error) {
      console.error('Error deleting annotation:', error);
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const getAnnotationDisplay = (type) => {
    const styles = {
      accurate: { bg: 'var(--bg-card)', color: 'var(--primary)', border: '1px solid var(--primary)' },
      review_needed: { bg: 'var(--bg-card)', color: '#d97706', border: '1px solid #d97706' },
      incorrect: { bg: 'var(--bg-card)', color: '#dc2626', border: '1px solid #dc2626' }
    };
    
    const labels = {
      accurate: '✓ Accurate',
      review_needed: '⚠ Review',
      incorrect: '✗ Incorrect'
    };
    
    return { style: styles[type] || {}, label: labels[type] || 'No Annotation' };
  };

  if (isEditing) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {/* Annotation Type Buttons */}
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {['accurate', 'review_needed', 'incorrect'].map(type => (
            <button
              key={type}
              onClick={() => handleSave(type)}
              disabled={loading}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                borderRadius: '4px',
                border: '1px solid var(--border)',
                background: 'var(--bg-card)',
                color: 'var(--text)',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              {getAnnotationDisplay(type).label}
            </button>
          ))}
          <button
            onClick={() => setIsEditing(false)}
            style={{
              padding: '4px 8px',
              fontSize: '12px',
              borderRadius: '4px',
              border: '1px solid var(--border)',
              background: 'var(--bg-card)',
              color: 'var(--text-muted)',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        </div>

        {/* Notes Input */}
        <div style={{ display: 'flex', gap: '4px' }}>
          <input
            type="text"
            placeholder="Add notes (optional)..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={{
              flex: 1,
              padding: '4px 8px',
              fontSize: '12px',
              borderRadius: '4px',
              border: '1px solid var(--border)',
              background: 'var(--bg-card)',
              color: 'var(--text)'
            }}
          />
        </div>
      </div>
    );
  }

  if (annotation) {
    const display = getAnnotationDisplay(annotation);
    return (
      <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <span style={{
            padding: '4px 8px',
            fontSize: '12px',
            borderRadius: '4px',
            ...display.style
          }}>
            {display.label}
          </span>
          <button
            onClick={() => setIsEditing(true)}
            style={{
              padding: '2px 6px',
              fontSize: '10px',
              borderRadius: '3px',
              border: '1px solid var(--border)',
              background: 'var(--bg-card)',
              color: 'var(--text-muted)',
              cursor: 'pointer'
            }}
          >
            Edit
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            style={{
              padding: '2px 6px',
              fontSize: '10px',
              borderRadius: '3px',
              border: '1px solid var(--border)',
              background: 'var(--bg-card)',
              color: '#dc2626',
              cursor: 'pointer'
            }}
          >
            ×
          </button>
          {notes && (
            <button
              onClick={() => setShowNotes(!showNotes)}
              style={{
                padding: '2px 6px',
                fontSize: '10px',
                borderRadius: '3px',
                border: '1px solid var(--border)',
                background: 'var(--bg-card)',
                color: 'var(--text-muted)',
                cursor: 'pointer'
              }}
              title="Toggle notes"
            >
              📝
            </button>
          )}
        </div>
        
        {/* Notes Display */}
        {notes && showNotes && (
          <div style={{
            padding: '6px 8px',
            fontSize: '11px',
            background: 'var(--bg)',
            border: '1px solid var(--border)',
            borderRadius: '4px',
            color: 'var(--text-muted)',
            maxWidth: '200px',
            wordBreak: 'break-word'
          }}>
            <strong>Notes:</strong> {notes}
          </div>
        )}
      </div>
      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete annotation?"
        message="This action removes the saved annotation for this compound."
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        loading={loading}
      />
      </>
    );
  }

  return (
    <>
      <button
        onClick={() => setIsEditing(true)}
        style={{
          padding: '4px 8px',
          fontSize: '12px',
          borderRadius: '4px',
          border: '1px solid var(--border)',
          background: 'var(--bg-card)',
          color: 'var(--text-muted)',
          cursor: 'pointer'
        }}
      >
        + Add
      </button>
      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete annotation?"
        message="This action removes the saved annotation for this compound."
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        loading={loading}
      />
    </>
  );
};

export default PredictionAnnotation;

